import { useMemo } from "react";
import type { StripeElementsOptions } from "@stripe/stripe-js";
import { usePaywallCheckoutContext } from "@/react/contexts/Paywalls/paywallCheckoutContext";
import { isOneTimePrice } from "@/react/helpers/paywallPriceHelpers";
import { customizedAppearance } from "../../StripeElements/stripeElementsOptions";
import { defaultAppearance } from "../../StripeElements/stripeElementsOptions";
import { stripeElementsModes } from "../../StripeElements/types";
import { getPaywallPriceAmount } from "../helpers/paywallPrices";

export const useOptions = () => {
  const {
    currentCommunityProcessorPaymentMethodConfigurationId:
      paymentMethodConfiguration,
    currentCommunityMemberPaymentProcessorSessionClientSecret:
      customerSessionClientSecret,
    paywall,
    getSelectedPrice,
    previewMutation,
    isPaywallsCheckoutCustomizationPhase1Enabled,
  } = usePaywallCheckoutContext();

  const appearance = isPaywallsCheckoutCustomizationPhase1Enabled
    ? customizedAppearance
    : defaultAppearance;

  const currency = paywall.currency.code;
  const selectedPrice = getSelectedPrice();
  const preview = previewMutation.data;

  const amount = getPaywallPriceAmount(selectedPrice, preview);

  // If the amount is 0 (either because it's free or trial), we won't confirm a payment intent
  // nor save the payment method. To guarantee that we save the payment method for future use when
  // required (e.g. paywall that requires a payment method for trial), we use the `setup` mode.
  const mode =
    amount === 0
      ? stripeElementsModes.setup
      : isOneTimePrice(selectedPrice)
        ? stripeElementsModes.payment
        : stripeElementsModes.subscription;

  return useMemo<StripeElementsOptions>(
    () => ({
      appearance,
      paymentMethodConfiguration,
      customerSessionClientSecret,
      mode,
      currency,
      amount,
    }),
    [
      appearance,
      paymentMethodConfiguration,
      customerSessionClientSecret,
      mode,
      currency,
      amount,
    ],
  );
};
